import { FolderListView } from "components/file-system/folder-view/FolderListView";
import { useCallback, useEffect, useState } from "react";
import { Dialog } from "components/dialog";
import { LibraryDialogContent } from "components/file-system/LibraryDialogContent";
import { BiLibrary } from "react-icons/bi";
import SearchAndNew from "components/file-system/SearchAndNewFileOrFolder";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { RotateCcw, Save } from "lucide-react";
import toast from "react-hot-toast";
import { useConfirmation } from "components/dialog/confirmation";
import { FileSystemItem } from "types";
import { handleAxiosAndCaughtError } from "utils";
import { PreviewDialog } from "chat/components/preview-dialog";
import DeepExtractFileView from "../../../../../library/file-or-folder-view/components/DeepExtractFileView";
import { useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Library } from "constants/library";

const DocumentsTab = () => {
  const { t } = useTranslation();
  const {
    knowledgeBase,
    setKnowledgeBase,
    updateAgentDocuments,
    orignalKnowledgeBase,
  } = useAgentInfo();

  const [searchValue, setSearchValue] = useState("");
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [newDocuments, setNewDocuments] = useState<FileSystemItem[]>([]);
  const [removeDocuments, setRemoveDocuments] = useState<FileSystemItem[]>([]);

  // Document preview state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<FileSystemItem | null>(null);

  // Custom confirmation dialog state
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isRegenerateSuggestions, setIsRegenerateSuggestions] = useState(true);

  const { confirm } = useConfirmation();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepExtract = searchParams.get("deepExtract");
  const [deepExtractDialogOpen, setDeepExtractDialogOpen] = useState(
    !!deepExtract
  );

  useEffect(() => {
    setDeepExtractDialogOpen(!!deepExtract);
  }, [deepExtract]);

  const getFileToShow = useCallback(
    (idOverride?: string | null) => {
      const idToFind = idOverride || deepExtract;
      if (!idToFind) return null;
      return knowledgeBase.find((f) => f.id === idToFind) || null;
    },
    [deepExtract, knowledgeBase]
  );
  const handleDialogClose = (deleteString?: string) => {
    setTimeout(() => {
      setSearchParams((params) => {
        params.delete(deleteString ?? "file");
        return params;
      });
    }, 200);
  };

  // Reset to original state
  const reset = async () => {
    const confirmed = await confirm({
      title: t("agents.knowledgeBase.resetChanges"),
      message: t("agents.knowledgeBase.resetChangesConfirm"),
    });

    if (confirmed) {
      setKnowledgeBase(orignalKnowledgeBase);
      setNewDocuments([]);
      setRemoveDocuments([]);
      toast.success(t("agents.knowledgeBase.resetSuccess"));
    }
  };

  const filteredItems = knowledgeBase
    .map((f) => ({
      ...f,
      document_metadata:
        typeof f.document_metadata === "string"
          ? // @ts-expect-error
            JSON.parse(f.document_metadata.replace(/'/g, '"'))
          : f.document_metadata,
      type: "Document" as const,
    }))
    .filter((f) => f.name.toLowerCase().includes(searchValue.toLowerCase()));

  const handleSubmit = () => {
    setIsConfirmDialogOpen(true);
  };

  const confirmSubmit = async () => {
    setIsConfirmDialogOpen(false);
    try {
      await updateAgentDocuments(
        newDocuments.map((i) => i.id),
        removeDocuments.map((i) => i.agent_document_id),
        isRegenerateSuggestions
      );
      setNewDocuments([]);
      setRemoveDocuments([]);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const handleDelete = async (removeDocument?: FileSystemItem) => {
    const confirmed = await confirm({
      title: t("agents.knowledgeBase.deleteFile"),
      message: t("agents.knowledgeBase.deleteFileConfirm"),
      variant: "danger",
    });

    if (confirmed) {
      if (!removeDocument) {
        setKnowledgeBase(
          knowledgeBase.filter(
            (kb) => !removeDocuments.some((doc) => doc.id === kb.id)
          )
        );
        return;
      }

      if (newDocuments.some((doc) => doc.id === removeDocument.id)) {
        setNewDocuments(
          newDocuments.filter((doc) => doc.id !== removeDocument.id)
        );
      } else if (!removeDocuments.some((doc) => doc.id === removeDocument.id)) {
        setRemoveDocuments([...removeDocuments, removeDocument]);
      }
      setKnowledgeBase(
        knowledgeBase.filter((doc) => doc.id !== removeDocument.id)
      );
    }
  };

  const handleFileClick = (removeDocument: FileSystemItem) => {
    const isInRemoveList = removeDocuments.some((doc) => {
      return doc.id === removeDocument.id;
    });

    if (isInRemoveList) {
      setRemoveDocuments(
        removeDocuments.filter((doc) => doc.id !== removeDocument.id)
      );
    } else {
      setRemoveDocuments([...removeDocuments, removeDocument]);
    }
  };

  // Handle document name click for preview
  const handleDocumentNameClick = (document: FileSystemItem) => {
    handleDocumentPreview(document);
  };

  // Handle document preview
  const handleDocumentPreview = (document: FileSystemItem) => {
    setSelectedDocument(document);
    setIsPreviewOpen(true);
  };

  const handleLibraryConfirm = (items: FileSystemItem[]) => {
    const toNewDocumentIds = items.filter(
      (i) => !orignalKnowledgeBase.some((f) => f.id === i.id)
    );

    const toRemoveDocumentIds = orignalKnowledgeBase.filter(
      (f) => !items.some((i) => i.id === f.id)
    );

    const mappedItems = [
      ...items.map((i) => {
        const { type, ...rest } = i;
        return {
          ...rest,
        } as any;
      }),
    ];
    setNewDocuments(toNewDocumentIds);
    setRemoveDocuments(toRemoveDocumentIds);
    setKnowledgeBase(mappedItems);
  };

  const hasChanges = newDocuments.length > 0 || removeDocuments.length > 0;

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex-1">
          <div>
            <SearchAndNew
              className="!mx-0 mb-3 md:mb-0"
              value={searchValue}
              onChange={(s) => setSearchValue(s)}
              showAdd={false}
            />
          </div>
        </div>
        <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-auto md:flex-row">
          <Dialog
            header={t("agents.knowledgeBase.selectNewDocuments")}
            subHeader={t("agents.knowledgeBase.updateAgentKnowledgeBase")}
            trigger={
              <button
                type="button"
                onClick={() => setIsLibraryOpen(true)}
                className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 md:w-auto"
              >
                <BiLibrary className="text-lg" />
                <span>{t("agents.knowledgeBase.updateFromLibrary")}</span>
              </button>
            }
            isOpen={isLibraryOpen}
            setIsOpen={setIsLibraryOpen}
            children={
              <LibraryDialogContent
                selectedItems={knowledgeBase}
                filterItem={(items) => {
                  return items.filter(
                    (item) =>
                      !orignalKnowledgeBase.some(
                        (originalItem) => originalItem.id === item.id
                      )
                  );
                }}
                handleConfirm={handleLibraryConfirm}
                setIsLibraryOpen={setIsLibraryOpen}
                rootFolderName={Library.DOCUMENTS}
              />
            }
          />
          <div className="flex gap-2">
            <button
              disabled={!hasChanges}
              onClick={handleSubmit}
              className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
            >
              <Save size={15} />
              <span>{t("common.save")}</span>
            </button>
            <button
              disabled={!hasChanges}
              onClick={reset}
              title={t("agents.knowledgeBase.resetToOriginal")}
              className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>
      </div>
      <div className="overflow-auto">
        <FolderListView
          selectedFiles={removeDocuments}
          setSelectedFiles={setRemoveDocuments}
          handleDelete={handleDelete}
          onFileClick={handleFileClick}
          handleNavigate={handleDocumentNameClick}
          willShowCheckbox={(item) => {
            return true;
          }}
          willUpdateUrl={false}
          fileItems={filteredItems}
          isDocumentFileType={true}
        />
      </div>

      {/* Document Preview Dialog */}
      <PreviewDialog
        isOpen={isPreviewOpen}
        setIsOpen={setIsPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        file={selectedDocument}
        page={1}
      />

      {/* Custom Confirmation Dialog with Checkbox */}
      <Dialog
        isOpen={isConfirmDialogOpen}
        setIsOpen={setIsConfirmDialogOpen}
        header={t("agents.knowledgeBase.updateTitle")}
        className="w-full max-w-md"
      >
        <div className="flex flex-col gap-4">
          <div className="text-gray-600 dark:text-gray-300">
            {newDocuments.length > 0 && removeDocuments.length > 0 && (
              <p>
                <Trans
                  i18nKey="agents.knowledgeBase.updateConfirmWithBoth"
                  values={{
                    addCount: newDocuments.length,
                    removeCount: removeDocuments.length,
                  }}
                  components={{
                    green: <span className="text-green-600 font-semibold" />,
                    red: <span className="text-red-600 font-semibold" />,
                  }}
                />
              </p>
            )}
            {newDocuments.length > 0 && removeDocuments.length === 0 && (
              <p>
                <Trans
                  i18nKey="agents.knowledgeBase.updateConfirmAdd"
                  values={{ addCount: newDocuments.length }}
                  components={{
                    green: <span className="text-green-600 font-semibold" />,
                  }}
                />
              </p>
            )}
            {newDocuments.length === 0 && removeDocuments.length > 0 && (
              <p>
                <Trans
                  i18nKey="agents.knowledgeBase.updateConfirmRemove"
                  values={{ removeCount: removeDocuments.length }}
                  components={{
                    red: <span className="text-red-600 font-semibold" />,
                  }}
                />
              </p>
            )}
            {newDocuments.length === 0 && removeDocuments.length === 0 && (
              <p>{t("agents.knowledgeBase.noChangesToSave")}</p>
            )}
          </div>

          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-500"
              checked={isRegenerateSuggestions}
              onChange={(e) => setIsRegenerateSuggestions(e.target.checked)}
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("agents.knowledgeBase.regenerateSuggestions")}
            </span>
          </label>

          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => setIsConfirmDialogOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={confirmSubmit}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              {t("common.confirm")}
            </button>
          </div>
        </div>
      </Dialog>
      <Dialog
        isOpen={deepExtractDialogOpen && Boolean(getFileToShow(deepExtract))}
        setIsOpen={setDeepExtractDialogOpen}
        onClose={() => handleDialogClose("deepExtract")}
        header="AI Deep Extract — Edit or Regenerate"
        headerClassName="sm:px-4 px-2 py-3 !border-none !mb-0"
        className="!h-screen !max-h-[100vh] !w-[90vw] !max-w-[90vw] !p-0"
      >
        <DeepExtractFileView file={getFileToShow(deepExtract)} />
      </Dialog>
    </>
  );
};

export default DocumentsTab;
